// PricingCommon.js - Shared imports, hooks, and utilities for Pricing Configuration
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { authFetch, authFetchRequest } from '../utils/api';
import { alertC, confirmC } from '../components/dialogService';
// Re-export all MUI components for use by tab components
export {
  React,
  useEffect,
  useState,
  useMemo,
  useCallback,
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  DataGrid,
  authFetch,
  authFetchRequest,
  alertC,
  confirmC
};

// ========== SHARED HOOKS ==========

// Hook to load attribute labels from database, optionally filtered by applies_to
export const useAttributeLabels = (appliesTo) => {
  const [allLabels, setAllLabels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authFetch('/pricing/attribute-labels')
      .then(data => {
        setAllLabels(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to fetch attribute labels:', err);
        setAllLabels([
          { value: 'doorData.finish.interiorColor.label', label: 'Frame internal color label', category: 'color', applies_to: 'all' },
          { value: 'doorData.finish.exteriorColor.label', label: 'Frame external color label', category: 'color', applies_to: 'all' },
          { value: 'doorData.frameData.shape1', label: 'Shape of the first section in the main row', category: 'shape', applies_to: 'all' }
        ]);
        setLoading(false);
      });
  }, []);

  const attributeLabels = useMemo(() => {
    if (!appliesTo || appliesTo === 'all') return allLabels;
    return allLabels.filter(a => a.applies_to === appliesTo || a.applies_to === 'all');
  }, [allLabels, appliesTo]);

  return { attributeLabels, loading };
};

// ========== SHARED CONSTANTS ==========

export const CONDITION_OPERATORS = [
  { value: '=', label: '=' },
  { value: '!=', label: '!=' },
  { value: '>', label: '>' },
  { value: '<', label: '<' },
  { value: '>=', label: '>=' },
  { value: '<=', label: '<=' },
  { value: 'in', label: 'in (list)' },
  { value: 'not_in', label: 'not in (list)' },
  { value: 'contains', label: 'contains' }
];

export const ACTION_TYPES = [
  { value: 'table', label: 'Use Price Table' },
  { value: 'formula', label: 'Apply Formula' },
  { value: 'surcharge', label: 'Add Surcharge' }
];

export const DIMENSION_LABELS = {
  area: 'Area (sqft)',
  perimeter: 'Perimeter (ft)',
  width: 'Width (in)',
  height: 'Height (in)',
  fixed: 'Fixed'
};
